import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getSchoolSeats } from '../../../actions/user.actions';
import { LicenseDasbhoardViewed } from '../../../ampli';
import { palette } from '../../../assets/styles/themes/tokens';
import { routes } from '../../../constants';
import {
	selectSelectedSchool,
	selectSelectedSchoolId,
	selectUser,
} from '../../../reducers/user.selectors';
import services from '../../../services';
import { SnackbarContext } from '../../../state/SnackbarContextProvider';
import { buildAmpliSubscriptionProducts } from '../../../utils/schoolSeats';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import { useRequestMoreSeats } from '../hooks/useRequestMoreSeats';
import { useRequestUpgradeProducts } from '../hooks/useRequestUpgradeProducts';

const PRODUCT = {
	BIBLIOTHEK: 'Bibliothek',
	FORTBILDUNGEN: 'Fortbildungen',
	KI_TOOLS: 'KI-Tools',
} as const;

type AddProductOption = (typeof PRODUCT)[keyof typeof PRODUCT];

const SchoolDashboardPage: React.FC = () => {
	const { router } = useRouter();
	const user = useSelector(selectUser);
	const selectedSchool = useSelector(selectSelectedSchool);
	const getSchoolSeatsAction = useAction(getSchoolSeats);
	const schoolSeats = useSelector((state: any) => state.user.schoolSeats);
	const { t } = useTranslation();
	const { openSnackbar } = useContext(SnackbarContext);
	const selectedSchoolId = useSelector(selectSelectedSchoolId);

	const { handleRequestMoreSeats } = useRequestMoreSeats();
	const { handleRequestUpgradeProducts } = useRequestUpgradeProducts();

	const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
	const [selectedAddProducts, setSelectedAddProducts] = useState<Set<AddProductOption>>(new Set());
	const [isRequestSeatsDialogOpen, setIsRequestSeatsDialogOpen] = useState(false);
	const [requestedSeatsCount, setRequestedSeatsCount] = useState<number | ''>('');

	const handleAddProductClick = () => {
		setSelectedAddProducts(new Set());
		setIsAddProductDialogOpen(true);
	};

	const handleAddProductCancel = () => {
		setIsAddProductDialogOpen(false);
		setSelectedAddProducts(new Set());
	};

	const toggleAddProductOption = (option: AddProductOption) => {
		setSelectedAddProducts((prev) => {
			const next = new Set(prev);
			if (next.has(option)) {
				next.delete(option);
			} else {
				next.add(option);
			}
			return next;
		});
	};

	const handleRequestSeatsClick = () => {
		setIsRequestSeatsDialogOpen(true);
		setRequestedSeatsCount(5);
	};

	const handleRequestSeatsSubmit = () => {
		const seatsCount =
			typeof requestedSeatsCount === 'number'
				? requestedSeatsCount
				: Number.parseInt(requestedSeatsCount);

		if (seatsCount && seatsCount > 0) {
			(handleRequestMoreSeats as (seatsCount?: number) => Promise<void>)(seatsCount);
			setIsRequestSeatsDialogOpen(false);
		}
	};

	const handleRequestSeatsCancel = () => {
		setIsRequestSeatsDialogOpen(false);
		setRequestedSeatsCount(5);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user && !user.isSchoolAdmin) {
			router.push(`/${routes.dashboard.root}`);
		}
	}, [user?.isSchoolAdmin]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user && user.isSchoolAdmin && selectedSchoolId) {
			try {
				getSchoolSeatsAction(selectedSchoolId).then((data: any) => {
					if (data.type !== 'GET_SCHOOL_SEATS_SUCCESS') {
						openSnackbar({
							type: 'error',
							message: t('Error while fetching school seats'),
						});
					}
				});
			} catch {
				openSnackbar({
					type: 'error',
					message: t('Error while fetching school seats'),
				});
			}
		}
	}, [user?.isSchoolAdmin, selectedSchoolId]);

	const sendSchoolDashboardViewedEvent = () => {
		if (user && user.isSchoolAdmin && schoolSeats && selectedSchoolId) {
			const subscriptionProducts = buildAmpliSubscriptionProducts(schoolSeats);

			services.track.ampliEventTrack({
				event: new LicenseDasbhoardViewed({
					'school id': selectedSchoolId,
					'subscription products': subscriptionProducts,
				}),
			});
		}
	};

	const lastTrackedSchoolId = useRef<string | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (
			user?.isSchoolAdmin &&
			schoolSeats &&
			selectedSchoolId &&
			lastTrackedSchoolId.current !== selectedSchoolId
		) {
			sendSchoolDashboardViewedEvent();
			lastTrackedSchoolId.current = selectedSchoolId;
		}
	}, [selectedSchoolId, schoolSeats]);

	const SeatCard = ({
		title,
		data,
	}: {
		title: string;
		data: { available: number; used: number; remaining: number };
	}) => (
		<Card elevation={2}>
			<CardContent>
				<Typography variant="h6">{title}</Typography>
				<Grid container spacing={2}>
					<Grid item xs={4}>
						<Typography variant="subtitle2" color="text.secondary">
							{t('Available')}
						</Typography>
						<Typography variant="h4" fontWeight={700}>
							{data.available}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="subtitle2" color="text.secondary">
							{t('Used')}
						</Typography>
						<Typography variant="h4" fontWeight={700}>
							{data.used}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="subtitle2" color="text.secondary">
							{t('Remaining')}
						</Typography>
						<Typography variant="h4" fontWeight={700} color={data.remaining < 0 ? 'error' : 'inherit'}>
							{data.remaining}
						</Typography>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);

	const MukiCard = ({ title, isActive }: { title: string; isActive: boolean }) => (
		<Card elevation={2}>
			<CardContent>
				<Typography variant="h6">{title}</Typography>
				<Typography variant="subtitle2" color="text.secondary">
					{t('Status')}
				</Typography>
				<Typography
					variant="h4"
					fontWeight={700}
					sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
				>
					{isActive ? t('Active') : t('Not active')}
					{!isActive && (
						<Tooltip title={t('KI-Tools inactive info tooltip')} arrow>
							<InfoOutlinedIcon
								sx={{
									fontSize: '1em',
									color: 'text.secondary',
									cursor: 'pointer',
								}}
							/>
						</Tooltip>
					)}
				</Typography>
			</CardContent>
		</Card>
	);

	const noKombi = (schoolSeats?.kombiSeats?.available ?? 0) === 0;
	const hasBibliothek = (schoolSeats?.bibliothekSeats?.available ?? 0) > 0;
	const hasFortbildungen = (schoolSeats?.courseSeats?.available ?? 0) > 0;
	const isMukiInactive = Boolean(schoolSeats) && !schoolSeats.isMukiEnabled;

	const exactlyOneMainProduct = hasBibliothek !== hasFortbildungen;

	const shouldShowUpgradeButton =
		isMukiInactive || (Boolean(schoolSeats) && noKombi && exactlyOneMainProduct);

	const addProductOptions: AddProductOption[] = (() => {
		if (!schoolSeats) return [];

		const hasBibliothek = schoolSeats.bibliothekSeats?.available > 0;
		const hasFortbildungen = schoolSeats.courseSeats?.available > 0;
		const options: AddProductOption[] = [];

		// If the school already has Bibliothek, offer Fortbildungen and Muki
		if (hasBibliothek && !hasFortbildungen) {
			options.push(PRODUCT.FORTBILDUNGEN);
		}

		// If the school already has Fortbildungen, offer Bibliothek and Muki
		if (hasFortbildungen && !hasBibliothek) {
			options.push(PRODUCT.BIBLIOTHEK);
		}

		// Hide KI-Tools if already active
		if (!schoolSeats.isMukiEnabled) {
			options.push(PRODUCT.KI_TOOLS);
		}

		return options;
	})();

	const getAddProductOptionLabel = (option: AddProductOption) => {
		switch (option) {
			case PRODUCT.BIBLIOTHEK:
				return t('Bibliothek Seats');
			case PRODUCT.FORTBILDUNGEN:
				return t('Course Seats');
			case PRODUCT.KI_TOOLS:
				return t('KI Tools (Muki)');
			default:
				return option;
		}
	};

	const handleAddProductConfirm = () => {
		const requestedProducts = addProductOptions.filter((opt) => selectedAddProducts.has(opt));
		setIsAddProductDialogOpen(false);
		setSelectedAddProducts(new Set());
		handleRequestUpgradeProducts(requestedProducts);
	};

	return (
		<Box
			className="root"
			paddingTop="10px"
			sx={{ minHeight: '100%', backgroundColor: 'rgb(249, 250, 251)' }}
		>
			<Box className="container">
				<Typography
					variant="h5"
					sx={{ color: palette.text.primary }}
					marginBottom="10px"
					marginTop="10px"
				>
					{t('School Dashboard')}
				</Typography>
				{schoolSeats && (
					<>
						<Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
							{(!user?.manageableSchools || user.manageableSchools.length === 0) && (
								<Typography variant="h6">
									{selectedSchool ? selectedSchool.name : schoolSeats.name} ({schoolSeats.accountNumber})
								</Typography>
							)}
							<Box display="flex" gap={2}>
								{shouldShowUpgradeButton && (
									<Button variant="contained" color="primary" onClick={handleAddProductClick}>
										{t('Add more products')}
									</Button>
								)}
								<Button variant="contained" color="success" onClick={handleRequestSeatsClick}>
									{t('Request More Seats')}
								</Button>
							</Box>
						</Box>

						<Stack spacing={3}>
							<MukiCard title={t('KI Tools (Muki)')} isActive={Boolean(schoolSeats?.isMukiEnabled)} />
							<SeatCard title={t('Bibliothek Seats')} data={schoolSeats.bibliothekSeats} />
							<SeatCard title={t('Course Seats')} data={schoolSeats.courseSeats} />
							<SeatCard title={t('Kombi Seats')} data={schoolSeats.kombiSeats} />
						</Stack>
					</>
				)}

				<Dialog open={isAddProductDialogOpen} onClose={handleAddProductCancel} maxWidth="sm" fullWidth>
					<DialogTitle>{t('Add more products')}</DialogTitle>
					<DialogContent>
						<Typography variant="body1" sx={{ mb: 2 }}>
							{t('Select products to add')}
						</Typography>

						<FormGroup>
							{addProductOptions.map((option) => (
								<FormControlLabel
									key={option}
									control={
										<Checkbox
											checked={selectedAddProducts.has(option)}
											onChange={() => toggleAddProductOption(option)}
										/>
									}
									label={getAddProductOptionLabel(option)}
								/>
							))}
						</FormGroup>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleAddProductCancel} color="inherit">
							{t('Cancel')}
						</Button>
						<Button
							onClick={handleAddProductConfirm}
							color="primary"
							variant="contained"
							disabled={selectedAddProducts.size === 0}
						>
							{t('Submit request')}
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog
					open={isRequestSeatsDialogOpen}
					onClose={handleRequestSeatsCancel}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>{t('Request More Seats')}</DialogTitle>
					<DialogContent>
						<Typography variant="body1" sx={{ mb: 2 }}>
							{t('How many more teachers should have access?')}
						</Typography>
						<TextField
							autoFocus
							margin="dense"
							label={t('Additional Seats Required')}
							type="number"
							fullWidth
							variant="outlined"
							value={requestedSeatsCount}
							onChange={(e) =>
								setRequestedSeatsCount(e.target.value ? Number.parseInt(e.target.value) : '')
							}
							inputProps={{ min: 1 }}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleRequestSeatsCancel} color="inherit">
							{t('Cancel')}
						</Button>
						<Button
							onClick={handleRequestSeatsSubmit}
							color="primary"
							variant="contained"
							disabled={
								!requestedSeatsCount ||
								(typeof requestedSeatsCount === 'number' && requestedSeatsCount <= 0)
							}
						>
							{t('Submit request')}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Box>
	);
};

export default SchoolDashboardPage;
